import HighlightedTable from 'components/HighlightedTable/HighlightedTable';
import './TeamRoutingPlans.css';
import { RoutingPlan } from 'schemas/RoutingPlan.schema';
import { DateTime } from 'luxon';
import { capitaliseFirstChar, conditionTargetToLabel, getMessagePlanStatusColour } from 'utils/formattingUtils';
import { AWARENESS_CAMPAIGN_TARGETS, ROUTING_COMMS_TARGETS, VACCINATION_CAMPAIGN_TARGETS } from 'schemas/enums/campaigns';
import { Select, Tag } from 'nhsuk-react-components';

interface RoutingPlansTableProps {
  routingPlans: RoutingPlan[];
  onCampaignTypeChange?: (routingPlanId: string, commsTarget: string) => void;
}

function RoutingPlansTable({
  routingPlans,
  onCampaignTypeChange,
}: RoutingPlansTableProps) {
  const commsTargets = [...VACCINATION_CAMPAIGN_TARGETS, ...AWARENESS_CAMPAIGN_TARGETS, ...ROUTING_COMMS_TARGETS];

  if (routingPlans.length === 0) {
    return null;
  }

  return (
    <HighlightedTable>
      <HighlightedTable.HeaderRow>
        <HighlightedTable.HeaderValue className="routing-plans-table__header-name">
          Name  (
          {routingPlans.length}
          )
        </HighlightedTable.HeaderValue>
        <HighlightedTable.HeaderValue className="routing-plans-table__header-last-updated">
          Last updated
        </HighlightedTable.HeaderValue>
        <HighlightedTable.HeaderValue className="routing-plans-table__header-status">
          Status
        </HighlightedTable.HeaderValue>
      </HighlightedTable.HeaderRow>
      {routingPlans
        .toSorted((a, b) => a.routing_plan_name.localeCompare(b.routing_plan_name))
        .map((plan) => (

          <HighlightedTable.Row key={plan.routing_plan_id} className={plan.status === 'draft' ? 'routing-plans-table__row--draft' : undefined}>
            <HighlightedTable.Value>
              <div className="nhsuk-u-font-weight-bold">
                {plan.routing_plan_name}
              </div>

              {(plan.status === 'draft' || plan.status === 'production') && (
                <div className="nhsuk-u-secondary-text-colour nhsuk-u-font-size-14">{plan.routing_plan_id}</div>
              )}

              <div>
                {onCampaignTypeChange ? (
                  <div className="nhsuk-u-margin-top-3 nhsuk-u-margin-bottom-1">
                    <Select
                      value={plan.comms_target ?? ''}
                      onChange={(event) => onCampaignTypeChange?.(plan.routing_plan_id, event.target.value)}
                    >
                      <Select.Option value="" key="use-campaign-target">
                        Use campaign target
                      </Select.Option>
                      {commsTargets.map((campaignTarget) => (
                        <Select.Option
                          value={campaignTarget}
                          key={campaignTarget}
                        >
                          {conditionTargetToLabel(campaignTarget)}
                        </Select.Option>
                      ))}
                    </Select>
                  </div>
                ) : (
                  <div className="nhsuk-u-secondary-text-colour nhsuk-u-font-size-14">
                    Target:
                    {plan.comms_target ? ` ${conditionTargetToLabel(plan.comms_target)}` : ' Use campaign target'}
                  </div>
                )}
              </div>

            </HighlightedTable.Value>
            <HighlightedTable.Value>
              {plan.created_at && (() => {
                const dt = DateTime.fromMillis(Number(plan.created_at));
                return (
                  <div className="nhsuk-u-secondary-text-colour nhsuk-u-font-size-16">
                    <div>{dt.toFormat('dd LLL yyyy')}</div>
                    <div>{dt.toFormat('HH:mm')}</div>
                  </div>
                );
              })()}
            </HighlightedTable.Value>
            <HighlightedTable.Value>
              <Tag modifier={getMessagePlanStatusColour(plan.status)}>{capitaliseFirstChar(plan.status)}</Tag>
            </HighlightedTable.Value>
          </HighlightedTable.Row>
        ))}
    </HighlightedTable>
  );
}

export default RoutingPlansTable;
